import { ILanguage, Trans, translate } from '@nicejob-library/internationalization';
import React, { FormEvent, ReactElement, useState } from 'react';
import styled from 'styled-components';
import { validatePassword as commonValidatePassword } from '../../../../common/validation/validatePassword';
import { PasswordField } from '../input/password-field/PasswordField';
import {
    FormDescription,
    HiddenInput,
    PasswordValidation,
    Row,
    RowSubmit,
    SubmitInput,
} from './Form.styles';

export interface AtomResetPasswordFormProps {
    email: string;
    code: string;
    is_multiple_platforms: boolean;
    post_url: string;
    language: ILanguage;

    onSubmit?: (e: FormEvent) => void;
}

const SavePassword = styled(SubmitInput)`
    width: 100%;
`;

export function AtomResetPasswordForm(props: AtomResetPasswordFormProps): ReactElement {
    const { code, email, is_multiple_platforms, post_url, onSubmit } = props;

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordMatch, setPasswordMatch] = useState(false);
    const [validation, setValidation] = useState({
        valid: false,
        message: '',
        color: '',
    });

    function validatePassword(value: string): void {
        const { valid, message, color } = commonValidatePassword({ password: value });
        setValidation({ valid, message, color });
    }

    function validatePasswordMatch(value: string, compare: string): void {
        const valid = value === compare;
        if (!valid) {
            setPasswordMatch(false);
        } else {
            setPasswordMatch(true);
        }
    }

    function onSubmitHandler(e: FormEvent): void {
        if (onSubmit) {
            onSubmit(e);
        }
    }

    return (
        <form onSubmit={onSubmitHandler} action={post_url} method='POST'>
            <FormDescription>
                {is_multiple_platforms ? (
                    <Trans
                        namespace='login'
                        k='reset_password.reset_your_password'
                        components={{
                            b: <b />,
                            br: <br />,
                        }}
                        params={{ email }}
                    />
                ) : (
                    translate({
                        namespace: 'login',
                        key: 'reset_password.new_password_form.description',
                        language: props.language,
                    })
                )}
            </FormDescription>

            <Row>
                <PasswordField
                    required
                    variant='stretch'
                    placeholder={translate({
                        namespace: 'login',
                        key: 'reset_password.new_password_form.placeholder',
                        language: props.language,
                    })}
                    onChange={(value: string): void => {
                        setPassword(value);
                        validatePassword(value);
                        validatePasswordMatch(value, confirmPassword);
                    }}
                />
                <PasswordValidation style={{ color: validation.color }}>
                    {validation.message}
                </PasswordValidation>
            </Row>

            <Row>
                <PasswordField
                    name='confirm_password'
                    required
                    variant='stretch'
                    placeholder={translate({
                        namespace: 'login',
                        key: 'reset_password.new_password_form.confirm_placeholder',
                        language: props.language,
                    })}
                    onChange={(value: string): void => {
                        setConfirmPassword(value);
                        validatePasswordMatch(value, password);
                    }}
                />
                {passwordMatch ? null : (
                    <PasswordValidation style={{ color: '#FF281F' }}>
                        {translate({
                            namespace: 'common',
                            key: 'validation.password.not_match',
                            language: props.language,
                        })}
                    </PasswordValidation>
                )}
            </Row>

            <RowSubmit>
                <SavePassword
                    type='submit'
                    disabled={!(passwordMatch && validation.valid)}
                    value={translate({
                        namespace: 'login',
                        key: 'reset_password.submit',
                        language: props.language,
                    })}
                />
            </RowSubmit>

            <HiddenInput type='text' name='code' value={code} />
            <HiddenInput type='text' name='skip_password_match' value={1} />
            <HiddenInput
                type='text'
                name='is_multiple_platforms'
                value={is_multiple_platforms ? 1 : 0}
            />
        </form>
    );
}
